import clsx from 'clsx';
import { HTMLProps, forwardRef } from 'react';

type SwitchSize = 'sm' | 'md';

interface SwitchProps extends Omit<HTMLProps<HTMLInputElement>, 'size'> {
  // Omit HTML size prop which expects a number
  className?: string;
  size?: SwitchSize;
}

const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  ({ className, checked, disabled, size = 'md', ...inputProps }, ref) => {
    const sizeStyles: Record<
      SwitchSize,
      { track: string; thumb: string; uncheckedLeft: string; checkedLeft: string }
    > = {
      md: {
        track: 'w-[43px] h-[25px]',
        thumb: 'w-[17.5px] h-[17.5px] top-[3.75px]',
        uncheckedLeft: 'left-[4px]',
        checkedLeft: 'left-[21.5px]',
      },
      sm: {
        track: 'w-[32px] h-[18.6px]',
        thumb: 'size-[12px] top-[3px]',
        uncheckedLeft: 'left-[3px]',
        checkedLeft: 'left-[17px]',
      },
    };

    const currentSize = sizeStyles[size];

    const trackBgColor =
      disabled && checked
        ? 'bg-primary-100' // Specific case: Disabled AND Checked
        : checked
          ? 'bg-primary-500' // Enabled AND Checked
          : disabled
            ? 'bg-gray-200' // Disabled AND Unchecked
            : 'bg-gray-2'; // Enabled AND Unchecked

    return (
      // Outer div (Track)
      <div
        className={clsx(
          'rounded-full flex relative items-center',
          'transition-colors duration-200 ease-in-out',
          currentSize.track,
          trackBgColor,
          className
        )}
      >
        {/* Inner div (Thumb) */}
        <div
          className={clsx(
            'absolute bg-white rounded-full ',
            'transition-all duration-200 ease-in-out',
            'shadow-sm',
            currentSize.thumb,
            checked ? currentSize.checkedLeft : currentSize.uncheckedLeft
          )}
        />

        {/* Hidden Input */}
        <input
          ref={ref}
          type="checkbox"
          role="switch"
          aria-checked={checked}
          className={clsx(
            'absolute inset-0 z-10 opacity-0',
            'w-full h-full',
            'cursor-pointer disabled:cursor-default'
          )}
          checked={checked}
          disabled={disabled}
          {...inputProps}
        />
      </div>
    );
  }
);

Switch.displayName = 'Switch';

export default Switch;
export type { SwitchSize };
